import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import {
  CreditCardTokenEntity,
  CardBrandType,
} from '../../../../../api/types/students/credit-card-tokens/credit-card-token-entity';

export interface CreditCardSelectionProps {
  creditCardTokens: CreditCardTokenEntity[];
  selectedCreditCardTokenId: number | null;
  onCreditCardChange: (tokenId: number | null) => void;
  onAddNewCardClick: () => void;
}

// Helper function to get card brand display name
const getCardBrandDisplayName = (brand: CardBrandType): string => {
  const brandNames: Record<CardBrandType, string> = {
    [CardBrandType.VISA]: 'ויזה',
    [CardBrandType.MASTERCARD]: 'מאסטרקארד',
    [CardBrandType.AMERICAN_EXPRESS]: 'אמריקן אקספרס',
    [CardBrandType.ISRACARD]: 'ישראכרט',
    [CardBrandType.DINERS_CLUB]: 'דיינרס קלאב',
    [CardBrandType.DISCOVER]: 'דיסקובר',
    [CardBrandType.MAESTRO]: 'מאסטרו',
    [CardBrandType.JCB]: 'JCB',
    [CardBrandType.PRIVATE_LABEL]: 'כרטיס פרטי',
  };
  return brandNames[brand] || brand;
};

// Helper function to get card brand icon
const getCardBrandIcon = (brand: CardBrandType): React.ReactNode => {
  const iconStyle = { width: '32px', height: '20px', marginLeft: '8px' };

  switch (brand) {
    case CardBrandType.VISA:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#1A1F71" />
          <path
            d="M19.5 21H17L18.8 11H21.3L19.5 21ZM15.2 11L12.8 18L12.5 16.5L11.6 12.1C11.6 12.1 11.5 11 10 11H6.1L6 11.2C6 11.2 7.7 11.5 9.7 12.7L11.8 21H14.4L18 11H15.2ZM35.3 21H37.5L35.6 11H33.6C32.4 11 32.1 11.9 32.1 11.9L28.2 21H30.8L31.3 19.5H34.5L34.8 21H35.3ZM32.1 17.5L33.5 13.8L34.3 17.5H32.1ZM27.8 13.7L28.2 11.3C28.2 11.3 26.8 10.8 25.3 10.8C23.7 10.8 20.8 11.5 20.8 14.1C20.8 16.5 24.2 16.5 24.2 17.8C24.2 19.1 21.2 18.7 19.9 17.7L19.5 20.2C19.5 20.2 20.9 20.8 23 20.8C25.1 20.8 27.9 19.6 27.9 17.2C27.9 14.7 24.5 14.5 24.5 13.4C24.5 12.3 26.7 12.5 27.8 13.7Z"
            fill="white"
          />
        </svg>
      );
    case CardBrandType.MASTERCARD:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#F5F5F5" />
          <circle cx="18" cy="16" r="9" fill="#EB001B" />
          <circle cx="30" cy="16" r="9" fill="#F79E1B" />
          <path
            d="M24 9.5C26.2 11.2 27.5 13.4 27.5 16C27.5 18.6 26.2 20.8 24 22.5C21.8 20.8 20.5 18.6 20.5 16C20.5 13.4 21.8 11.2 24 9.5Z"
            fill="#FF5F00"
          />
        </svg>
      );
    case CardBrandType.AMERICAN_EXPRESS:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#006FCF" />
          <path
            d="M8 16L10 12H12L14 16L16 12H18L15 18H13L11 14L9 18H7L8 16ZM18 12H24L25 14H21V15H24V16H21V17H25L24 19H18V12ZM27 12L29 16L31 12H33L30 18H28L25 12H27ZM33 12H39L40 14H36V15H39V16H36V17H40L39 19H33V12Z"
            fill="white"
          />
        </svg>
      );
    case CardBrandType.ISRACARD:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#003087" />
          <text
            x="24"
            y="19"
            textAnchor="middle"
            fill="white"
            fontSize="10"
            fontWeight="bold"
          >
            ישראכרט
          </text>
        </svg>
      );
    case CardBrandType.DINERS_CLUB:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#0079BE" />
          <circle cx="24" cy="16" r="10" fill="white" />
          <path
            d="M18 16C18 12.7 20.2 10 23 9.2V22.8C20.2 22 18 19.3 18 16ZM25 9.2C27.8 10 30 12.7 30 16C30 19.3 27.8 22 25 22.8V9.2Z"
            fill="#0079BE"
          />
        </svg>
      );
    case CardBrandType.DISCOVER:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#FF6600" />
          <ellipse cx="30" cy="16" rx="8" ry="7" fill="white" />
          <text x="14" y="19" fill="white" fontSize="8" fontWeight="bold">
            DISCOVER
          </text>
        </svg>
      );
    case CardBrandType.MAESTRO:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#F5F5F5" />
          <circle cx="18" cy="16" r="9" fill="#0066CC" />
          <circle cx="30" cy="16" r="9" fill="#CC0000" />
          <path
            d="M24 9.5C26.2 11.2 27.5 13.4 27.5 16C27.5 18.6 26.2 20.8 24 22.5C21.8 20.8 20.5 18.6 20.5 16C20.5 13.4 21.8 11.2 24 9.5Z"
            fill="#6600CC"
          />
        </svg>
      );
    case CardBrandType.JCB:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#F5F5F5" />
          <rect x="12" y="8" width="8" height="16" rx="2" fill="#0E4C96" />
          <rect x="20" y="8" width="8" height="16" rx="2" fill="#E31837" />
          <rect x="28" y="8" width="8" height="16" rx="2" fill="#007940" />
        </svg>
      );
    default:
      return (
        <i
          className="pi pi-credit-card"
          style={{ fontSize: '20px', marginLeft: '8px', color: '#666' }}
        />
      );
  }
};

interface CreditCardOption {
  label: string;
  value: number;
  brand: CardBrandType;
  last4: string;
  expiry: string;
}

// Credit card dropdown item template
const creditCardOptionTemplate = (option: CreditCardOption) => {
  if (!option) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', direction: 'rtl' }}>
      {getCardBrandIcon(option.brand)}
      <span style={{ flex: 1 }}>
        {getCardBrandDisplayName(option.brand)} **** **** **** {option.last4}
      </span>
      <span style={{ color: '#666', fontSize: '12px', marginRight: '8px' }}>
        ({option.expiry})
      </span>
    </div>
  );
};

// Credit card selected value template
const creditCardValueTemplate = (option: CreditCardOption | null) => {
  if (!option) return <span>בחר כרטיס אשראי</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', direction: 'rtl' }}>
      {getCardBrandIcon(option.brand)}
      <span>
        {getCardBrandDisplayName(option.brand)} **** **** **** {option.last4}
      </span>
      <span style={{ color: '#666', fontSize: '12px', marginRight: '8px' }}>
        ({option.expiry})
      </span>
    </div>
  );
};

export const CreditCardSelection: React.FC<CreditCardSelectionProps> = ({
  creditCardTokens,
  selectedCreditCardTokenId,
  onCreditCardChange,
  onAddNewCardClick,
}) => {
  const inputStyle = {
    width: '100%',
  };

  const creditCardOptions: CreditCardOption[] = creditCardTokens.map(
    (token) => ({
      label: `${getCardBrandDisplayName(token.cardBrand)} **** **** **** ${token.cardLast4}`,
      value: token.id,
      brand: token.cardBrand,
      last4: token.cardLast4,
      expiry: `${token.expiryMonth.toString().padStart(2, '0')}/${token.expiryYear}`,
    }),
  );

  return (
    <div
      style={{
        backgroundColor: '#E8F5E9',
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid #A5D6A7',
        marginBottom: '8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px',
        }}
      >
        <i
          className="pi pi-credit-card"
          style={{ color: '#388E3C', fontSize: '20px' }}
        ></i>
        <span
          style={{
            fontWeight: 'bold',
            color: '#388E3C',
            fontSize: '16px',
          }}
        >
          בחירת כרטיס אשראי
        </span>
      </div>

      <div className="flex flex-col">
        <label className="mb-2 font-medium">כרטיס לחיוב</label>
        <div
          style={{
            display: 'flex',
            gap: '12px',
            flexDirection: 'row',
            alignItems: 'stretch',
          }}
        >
          <Dropdown
            style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
            value={selectedCreditCardTokenId}
            options={creditCardOptions}
            onChange={(e) => onCreditCardChange(e.value)}
            placeholder="בחר כרטיס אשראי"
            emptyMessage="לא נמצאו כרטיסי אשראי"
            itemTemplate={creditCardOptionTemplate}
            valueTemplate={creditCardValueTemplate}
          />
          <Button
            type="button"
            icon="pi pi-plus"
            label="הוסף כרטיס חדש"
            className="p-button-outlined"
            style={{
              whiteSpace: 'nowrap',
              borderColor: '#388E3C',
              color: '#388E3C',
              height: 'auto',
            }}
            onClick={onAddNewCardClick}
          />
        </div>
      </div>

      {!selectedCreditCardTokenId && creditCardTokens.length === 0 && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#FFF3E0',
            borderRadius: '6px',
            border: '1px solid #FFB74D',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <i
              className="pi pi-exclamation-triangle"
              style={{ color: '#F57C00', fontSize: '16px' }}
            ></i>
            <span style={{ fontSize: '14px', color: '#E65100' }}>
              לא נמצאו כרטיסי אשראי שמורים. יש להוסיף כרטיס חדש.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
